/* eslint-disable max-lines */
import {
  IFocusOnParams,
  IMapControllerParams,
  IMapStatePayload,
  IReleaseAreaParams,
  IMoveToParams,
  IReserveAreaParams,
  IReservedAreaHandle,
  TInitialFitView,
} from './types';
import { clampVisibleBoundsToRestrictArea } from './utils/clampVisibleBoundsToRestrictArea';
import { buildAreaSpec, createSmoothMoveOptions, swallowRejection } from './utils/mapControllerActions';
import { fromGlobalPixels, toGlobalPixels } from '../../common/utils/projection';
import { isUserDrivenAction } from '../../common/utils/ymaps/isUserDrivenAction';

export type {
  IFocusOnParams,
  IMapControllerParams,
  IMapStatePayload,
  IReleaseAreaParams,
  IMoveToParams,
  IReserveAreaParams,
  IReservedAreaHandle,
} from './types';

interface IReservedAreaRecord extends IReservedAreaHandle {
  readonly accessor: YMaps.map.margin.Accessor;
  readonly width: number;
}

interface IVisibleAreaSnapshot {
  center: YMaps.TCoord;
  featureBounds: [YMaps.TCoord, YMaps.TCoord] | null;
  visibleSize: YMaps.TPixelCoord;
  zoom: number;
}

type TFrameHandle = number | ReturnType<typeof globalThis.setTimeout>;

const MARGIN_CHANGE_RESTORE_FALLBACK_MS = 80;
const FRAME_FALLBACK_DELAY_MS = 16;
// Мгновенное (без анимации) вписывание стартового вида с учётом margin под листинг.
const FIT_VIEW_MOVE_OPTIONS = { useMapMargin: true, duration: 0 };
const VISIBLE_FEATURES_ZOOM_MARGIN = 60;

export class MapController {
  protected readonly map: YMaps.Map;
  protected readonly onUserIntent: (payload: IMapStatePayload) => void;
  private readonly visibleRestrictArea?: [YMaps.TCoord, YMaps.TCoord];
  private readonly handleActionEnd: (event: YMaps.IEvent) => void;
  private readonly reservedAreas = new Set<IReservedAreaRecord>();
  private readonly pendingVisibleAreaRestoreCleanups = new Set<() => void>();
  private isDestroyed = false;
  private visibleAreaRestoreId = 0;

  public constructor({ map, onUserIntent, visibleRestrictArea }: IMapControllerParams) {
    this.map = map;
    this.onUserIntent = onUserIntent;
    this.visibleRestrictArea = visibleRestrictArea;

    this.handleActionEnd = (event: YMaps.IEvent): void => {
      if (this.isDestroyed) {
        return;
      }

      try {
        if (isUserDrivenAction(event.get<unknown>('action'))) {
          if (
            this.returnVisibleAreaToRestrictArea(() => {
              if (!this.isDestroyed) {
                this.notifyUserIntent();
              }
            })
          ) {
            return;
          }

          this.notifyUserIntent();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[MapController] actionend handler error', error);
      }
    };

    this.map.events.add('actionend', this.handleActionEnd);
  }

  public zoomIn(onApplied?: () => void): void {
    const promise = this.map.setZoom(this.map.getZoom() + 1, { useMapMargin: true });
    this.runOnAppliedAfterVisibleRestrict(promise, onApplied);
    swallowRejection(promise);
  }

  public zoomOut(onApplied?: () => void): void {
    const promise = this.map.setZoom(this.map.getZoom() - 1, { useMapMargin: true });
    this.runOnAppliedAfterVisibleRestrict(promise, onApplied);
    swallowRejection(promise);
  }

  public focusOn({ center, zoom, options, onApplied }: IFocusOnParams): void {
    const promise = this.map.setCenter(center, zoom, { ...createSmoothMoveOptions(), ...options, useMapMargin: true });
    this.runOnAppliedAfterVisibleRestrict(promise, onApplied);
    swallowRejection(promise);
  }

  public moveTo({ bounds, onApplied }: IMoveToParams): void {
    const correctedBounds = this.getVisibleRestrictBounds(bounds) ?? bounds;
    const promise = this.map.setBounds(correctedBounds, { useMapMargin: true });
    this.runOnAppliedAfterVisibleRestrict(promise, onApplied);
    swallowRejection(promise);
  }

  public getZoom(): number {
    return this.map.getZoom();
  }

  public getZoomRange(): readonly [number, number] {
    return this.map.zoomRange.getCurrent();
  }

  public getSnapshot(): { bounds: [YMaps.TCoord, YMaps.TCoord]; zoom: number } {
    return {
      bounds: this.map.getBounds({ useMapMargin: true }),
      zoom: this.map.getZoom(),
    };
  }

  public correctVisibleAreaToRestrictArea(onApplied?: () => void): boolean {
    return this.returnVisibleAreaToRestrictArea(onApplied);
  }

  public getCorrectedVisibleBounds(bounds: [YMaps.TCoord, YMaps.TCoord]): [YMaps.TCoord, YMaps.TCoord] | null {
    return this.getVisibleRestrictBounds(bounds);
  }

  public reserveAreaWithoutReload({
    onApplied,
    side,
    width,
    restoreVisibleArea = true,
    visibleFeatureCoordinates,
    fitView,
  }: IReserveAreaParams): IReservedAreaHandle {
    // initial (fitView задан): после addArea вписываем заданный вид в видимую (margin) область.
    // динамика (fitView нет): сохраняем текущий вид через restoreVisibleAreaAfterMarginUpdate.
    const visibleArea = !fitView && restoreVisibleArea ? this.captureVisibleArea(visibleFeatureCoordinates) : null;

    if (visibleArea) {
      this.restoreVisibleAreaAfterMarginUpdate(visibleArea, onApplied);
    }

    const accessor = this.map.margin.addArea(buildAreaSpec(side, width));
    const record: IReservedAreaRecord = {
      __brand: 'reserved-area-handle',
      accessor,
      width,
    };
    this.reservedAreas.add(record);

    if (fitView) {
      this.fitViewWithMargin(fitView);
      onApplied?.();
    } else if (!visibleArea) {
      onApplied?.();
    }

    return record;
  }

  private fitViewWithMargin(fitView: TInitialFitView): void {
    const promise =
      'bounds' in fitView
        ? this.map.setBounds(fitView.bounds, FIT_VIEW_MOVE_OPTIONS)
        : this.map.setCenter(fitView.center, fitView.zoom, FIT_VIEW_MOVE_OPTIONS);

    swallowRejection(promise);
  }

  public releaseAreaWithoutReload(
    handle: IReservedAreaHandle,
    { onApplied, restoreVisibleArea = true, visibleFeatureCoordinates }: IReleaseAreaParams = {},
  ): void {
    const record = handle as IReservedAreaRecord;

    if (!this.reservedAreas.has(record)) {
      return;
    }

    if (restoreVisibleArea) {
      const visibleArea = this.captureVisibleArea(visibleFeatureCoordinates);
      this.restoreVisibleAreaAfterMarginUpdate(visibleArea, onApplied);
    }

    record.accessor.remove();
    this.reservedAreas.delete(record);

    if (!restoreVisibleArea) {
      onApplied?.();
    }
  }

  private captureVisibleBounds(): [YMaps.TCoord, YMaps.TCoord] {
    return this.map.getBounds({ useMapMargin: true });
  }

  private captureVisibleArea(visibleFeatureCoordinates?: readonly YMaps.TCoord[]): IVisibleAreaSnapshot {
    return {
      center: this.map.getCenter({ useMapMargin: true }),
      featureBounds: this.getFeatureBounds(visibleFeatureCoordinates),
      visibleSize: this.getVisibleSize(),
      zoom: this.map.getZoom(),
    };
  }

  private getReservedWidth(): number {
    return Array.from(this.reservedAreas).reduce((sum, record) => sum + record.width, 0);
  }

  private getVisibleSize(): YMaps.TPixelCoord {
    const [containerWidth, containerHeight] = this.map.container.getSize();
    const reservedWidth = this.getReservedWidth();

    return [Math.max(containerWidth - reservedWidth, 1), Math.max(containerHeight, 1)];
  }

  private clampZoom(zoom: number): number {
    const [minZoom, maxZoom] = this.map.zoomRange.getCurrent();

    return Math.min(Math.max(zoom, minZoom), maxZoom);
  }

  private getIntegerZoomForWidthRatio(zoom: number, widthRatio: number): number {
    const targetZoom = zoom + Math.log2(widthRatio);
    const integerZoom = widthRatio < 1 ? Math.floor(targetZoom) : Math.ceil(targetZoom);

    return this.clampZoom(integerZoom);
  }

  private restoreVisibleBounds(bounds: [YMaps.TCoord, YMaps.TCoord], onApplied?: () => void): void {
    this.runRestoreMove(() => this.map.setBounds(bounds, createSmoothMoveOptions()), onApplied);
  }

  private restoreFeatureBounds(bounds: [YMaps.TCoord, YMaps.TCoord], onApplied?: () => void): void {
    this.runRestoreMove(
      () =>
        this.map.setBounds(bounds, {
          ...createSmoothMoveOptions(),
          zoomMargin: VISIBLE_FEATURES_ZOOM_MARGIN,
        }),
      onApplied,
    );
  }

  private restoreVisibleAreaAfterMarginUpdate(visibleArea: IVisibleAreaSnapshot, onApplied?: () => void): void {
    this.visibleAreaRestoreId += 1;
    const restoreId = this.visibleAreaRestoreId;
    let isRestoreStarted = false;
    let fallbackTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
    let cancelFrames: (() => void) | undefined;
    const startRestoreRef = {
      current: (): void => undefined,
    };

    const handleMarginChange = (): void => {
      startRestoreRef.current();
    };

    const cleanup = (): void => {
      if (fallbackTimer !== undefined) {
        globalThis.clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }

      cancelFrames?.();
      cancelFrames = undefined;
      this.map.events.remove('marginchange', handleMarginChange);
      this.pendingVisibleAreaRestoreCleanups.delete(cleanup);
    };

    const startRestore = (): void => {
      if (isRestoreStarted) {
        return;
      }

      isRestoreStarted = true;
      cleanup();
      cancelFrames = this.runAfterTwoFrames(() => {
        cancelFrames = undefined;
        this.pendingVisibleAreaRestoreCleanups.delete(cleanup);

        if (this.isDestroyed || restoreId !== this.visibleAreaRestoreId) {
          return;
        }

        this.restoreVisibleArea(visibleArea, onApplied);
      });
      this.pendingVisibleAreaRestoreCleanups.add(cleanup);
    };
    startRestoreRef.current = startRestore;

    this.pendingVisibleAreaRestoreCleanups.add(cleanup);
    this.map.events.once('marginchange', handleMarginChange);
    fallbackTimer = globalThis.setTimeout(startRestore, MARGIN_CHANGE_RESTORE_FALLBACK_MS);
  }

  private runAfterTwoFrames(callback: () => void): () => void {
    const requestFrame =
      typeof globalThis.requestAnimationFrame === 'function'
        ? globalThis.requestAnimationFrame.bind(globalThis)
        : (frameCallback: FrameRequestCallback) =>
            globalThis.setTimeout(() => frameCallback(Date.now()), FRAME_FALLBACK_DELAY_MS);
    const cancelFrame =
      typeof globalThis.cancelAnimationFrame === 'function'
        ? (handle: TFrameHandle) => globalThis.cancelAnimationFrame(handle as number)
        : (handle: TFrameHandle) => globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>);

    let firstFrame: TFrameHandle | undefined;
    let secondFrame: TFrameHandle | undefined;

    firstFrame = requestFrame(() => {
      secondFrame = requestFrame(() => {
        firstFrame = undefined;
        secondFrame = undefined;
        callback();
      });
    });

    return () => {
      if (firstFrame !== undefined) {
        cancelFrame(firstFrame);
        firstFrame = undefined;
      }

      if (secondFrame !== undefined) {
        cancelFrame(secondFrame);
        secondFrame = undefined;
      }
    };
  }

  private restoreVisibleArea(visibleArea: IVisibleAreaSnapshot, onApplied?: () => void): void {
    const visibleSize = this.getVisibleSize();
    const widthRatio = visibleSize[0] / visibleArea.visibleSize[0];
    const targetZoom = this.getIntegerZoomForWidthRatio(visibleArea.zoom, widthRatio);
    const targetBounds = this.getVisibleBoundsForCenter(visibleArea.center, targetZoom, visibleSize);

    if (visibleArea.featureBounds && !this.containsBounds(targetBounds, visibleArea.featureBounds)) {
      this.restoreFeatureBounds(visibleArea.featureBounds, onApplied);

      return;
    }

    this.runRestoreMove(() => this.map.setCenter(visibleArea.center, targetZoom, createSmoothMoveOptions()), onApplied);
  }

  private getFeatureBounds(visibleFeatureCoordinates?: readonly YMaps.TCoord[]): [YMaps.TCoord, YMaps.TCoord] | null {
    const validCoordinates = visibleFeatureCoordinates?.filter(this.isValidCoordinate) ?? [];

    if (validCoordinates.length === 0) {
      return null;
    }

    let south = validCoordinates[0][0];
    let north = validCoordinates[0][0];
    let west = validCoordinates[0][1];
    let east = validCoordinates[0][1];

    validCoordinates.forEach(([latitude, longitude]) => {
      south = Math.min(south, latitude);
      north = Math.max(north, latitude);
      west = Math.min(west, longitude);
      east = Math.max(east, longitude);
    });

    return [
      [south, west],
      [north, east],
    ];
  }

  private isValidCoordinate(coordinates: YMaps.TCoord): boolean {
    return Number.isFinite(coordinates[0]) && Number.isFinite(coordinates[1]);
  }

  private getVisibleBoundsForCenter(
    center: YMaps.TCoord,
    zoom: number,
    visibleSize: YMaps.TPixelCoord,
  ): [YMaps.TCoord, YMaps.TCoord] {
    const [centerPixelX, centerPixelY] = toGlobalPixels(center, zoom);
    const [visibleWidth, visibleHeight] = visibleSize;
    const halfWidth = visibleWidth / 2;
    const halfHeight = visibleHeight / 2;

    return [
      fromGlobalPixels([centerPixelX - halfWidth, centerPixelY + halfHeight], zoom),
      fromGlobalPixels([centerPixelX + halfWidth, centerPixelY - halfHeight], zoom),
    ];
  }

  private containsBounds(
    outerBounds: [YMaps.TCoord, YMaps.TCoord],
    innerBounds: [YMaps.TCoord, YMaps.TCoord],
  ): boolean {
    const [outerSouthWest, outerNorthEast] = outerBounds;
    const [innerSouthWest, innerNorthEast] = innerBounds;

    return (
      outerSouthWest[0] <= innerSouthWest[0] &&
      innerNorthEast[0] <= outerNorthEast[0] &&
      outerSouthWest[1] <= innerSouthWest[1] &&
      innerNorthEast[1] <= outerNorthEast[1]
    );
  }

  private runRestoreMove(createMovePromise: () => Promise<unknown> | undefined, onApplied?: () => void): void {
    const promise = createMovePromise();

    if (!promise || typeof promise.then !== 'function') {
      return;
    }

    promise.then(
      () => {
        if (!this.isDestroyed) {
          onApplied?.();
        }
      },
      () => undefined,
    );
  }

  private getVisibleRestrictBounds(bounds: [YMaps.TCoord, YMaps.TCoord]): [YMaps.TCoord, YMaps.TCoord] | null {
    if (!this.visibleRestrictArea) {
      return null;
    }

    return clampVisibleBoundsToRestrictArea(bounds, this.visibleRestrictArea);
  }

  private returnVisibleAreaToRestrictArea(onApplied?: () => void): boolean {
    if (this.isDestroyed) {
      return false;
    }

    const restrictedVisibleBounds = this.getVisibleRestrictBounds(this.captureVisibleBounds());

    if (!restrictedVisibleBounds) {
      return false;
    }

    this.restoreVisibleBounds(restrictedVisibleBounds, onApplied);

    return true;
  }

  private runOnAppliedAfterVisibleRestrict(
    promise: Promise<unknown> | undefined,
    onApplied: (() => void) | undefined,
  ): void {
    if (!promise || typeof promise.then !== 'function') {
      return;
    }

    promise.then(
      () => {
        if (this.isDestroyed) {
          return;
        }

        if (this.returnVisibleAreaToRestrictArea(onApplied)) {
          return;
        }

        onApplied?.();
      },
      () => undefined,
    );
  }

  private notifyUserIntent(): void {
    try {
      this.onUserIntent({
        visibleBounds: this.map.getBounds({ useMapMargin: true }),
        fullBounds: this.map.getBounds(),
        zoom: this.map.getZoom(),
        center: this.map.getCenter(),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[MapController] actionend handler error', error);
    }
  }

  public destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.isDestroyed = true;
    this.visibleAreaRestoreId += 1;
    this.pendingVisibleAreaRestoreCleanups.forEach(cleanup => cleanup());
    this.map.events.remove('actionend', this.handleActionEnd);

    this.reservedAreas.forEach(record => record.accessor.remove());
    this.reservedAreas.clear();
  }
}
